/* ============================================================================
   Curated Collection JS – v2025-01-19-FINAL-WORKING
   ── Fetches pinned product + extracts handles from collection HTML
   ── All products rendered as consistent grid items
============================================================================ */

(() => {
  'use strict';
  
  const GRID_SEL = 'ul#product-grid, ul[id^="product-grid"], .product-grid';
  
  /* ------------------------------------------------------------------ */
  /* 0. Query-string parsing                                            */
  /* ------------------------------------------------------------------ */
  const qp = new URLSearchParams(location.search);

  let rawFirst = decodeURIComponent((qp.get('first') || '').trim());
  let firstHandle = '';
  let variantFromURL = '';

  if (rawFirst.startsWith('http')) {
    try {
      const u = new URL(rawFirst);
      const segs = u.pathname.split('/').filter(Boolean);
      if (segs[0] === 'products' && segs[1]) firstHandle = segs[1];
      variantFromURL = u.searchParams.get('variant') || '';
    } catch (_) {}
  } else {
    const segs = rawFirst.split('/').filter(Boolean);
    firstHandle = segs.pop();
  }
  if (!firstHandle) return;

  /* variant param (digits only) */
  const variantID = (/^\d+$/.test(qp.get('variant') || variantFromURL))
    ? (qp.get('variant') || variantFromURL)
    : '';

  /* source collection */
  let source = (qp.get('source') || '').trim();
  if (!source) {
    const p = location.pathname.split('/').filter(Boolean);
    if (p[0] === 'collections' && p[1]) source = p[1];
  }
  if (!source) return;

  /* limit */
  const limit = Math.max(1, parseInt(qp.get('limit') || '24', 10));

  const grid = document.querySelector(GRID_SEL);
  if (!grid) return;

  /* ------------------------------------------------------------------ */
  /* 1. Helpers                                                         */
  /* ------------------------------------------------------------------ */
  
  const updateCount = () => {
    const c = grid.querySelectorAll('.grid__item').length;
    document.querySelectorAll('[data-product-count], .product-count, .collection-product-count')
      .forEach(el => el.textContent = `${c} product${c === 1 ? '' : 's'}`);
  };

  const fetchCard = async (handle, variant = '') => {
    try {
      const url = `/products/${encodeURIComponent(handle)}?view=card${variant ? `&variant=${variant}` : ''}`;
      const response = await fetch(url, { cache: 'force-cache' });
      if (!response.ok) return null;
      
      const html = await response.text();
      
      // Parse HTML and extract the LI element
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const li = doc.body.querySelector('li.grid__item');
      
      return li ? li.outerHTML : null;
      
    } catch (error) {
      console.error(`Error fetching card for ${handle}:`, error);
      return null;
    }
  };

  const extractHandlesFromCollection = async () => {
    try {
      const response = await fetch(`/collections/${encodeURIComponent(source)}`, { cache: 'force-cache' });
      if (!response.ok) return [];
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find all product links in the grid
      const sourceGrid = doc.querySelector(GRID_SEL);
      if (!sourceGrid) return [];
      
      const handles = [];
      const links = sourceGrid.querySelectorAll('a[href*="/products/"]');
      
      links.forEach(link => {
        try {
          const url = new URL(link.href, location.origin);
          const segs = url.pathname.split('/').filter(Boolean);
          if (segs[0] === 'products' && segs[1]) {
            const handle = segs[1];
            if (!handles.includes(handle)) {
              handles.push(handle);
            }
          }
        } catch (_) {}
      });
      
      return handles;
      
    } catch (error) {
      console.error('Error extracting handles:', error);
      return [];
    }
  };

  const render = html => { 
    if (html && html.trim()) {
      grid.insertAdjacentHTML('beforeend', html);
      
      // Remove ratings from the last inserted item
      const lastItem = grid.lastElementChild;
      if (lastItem) {
        // Remove rating elements (stars/reviews)
        const ratings = lastItem.querySelectorAll('.rating, [class*="rating"], .card__information .stars, .product-rating, .judgeme-widget, .spr-badge');
        ratings.forEach(el => el.remove());
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* 2. Main Logic                                                      */
  /* ------------------------------------------------------------------ */
  
  (async () => {
    // Show loading
    grid.innerHTML = '<li class="grid__item" style="padding: 2rem; text-align: center; grid-column: 1/-1;">Loading…</li>';
    
    // Fetch pinned card and collection handles in parallel
    const [pinnedHTML, allHandles] = await Promise.all([
      fetchCard(firstHandle, variantID),
      extractHandlesFromCollection()
    ]);
    
    // Clear loading message
    grid.innerHTML = '';
    
    // Render pinned card first
    if (pinnedHTML) {
      render(pinnedHTML);
    } else {
      grid.innerHTML = '<li class="grid__item" style="padding: 2rem; grid-column: 1/-1;">Product not found</li>';
      return;
    }
    
    // Get remaining handles (excluding pinned)
    const seen = new Set([firstHandle]);
    const restHandles = [];
    
    for (const h of allHandles) {
      if (h && !seen.has(h)) {
        seen.add(h);
        restHandles.push(h);
      }
      if (restHandles.length >= (limit - 1)) break; // -1 because we already have the pinned one
    }
    
    // Fetch and render remaining cards
    const restCards = await Promise.all(
      restHandles.map(h => fetchCard(h))
    );
    
    restCards.forEach(render);
    updateCount();
    
    /* ------------------------------------------------------------------ */
    /* 3. Variant handling for pinned product                            */
    /* ------------------------------------------------------------------ */
    
    if (variantID) {
      try {
        const prod = await fetch(`/products/${firstHandle}.js`).then(r => r.json());
        const varObj = prod.variants.find(v => String(v.id) === variantID);
        if (!varObj || !varObj.featured_image) return;

        const newSrcBase = varObj.featured_image.src;
        const pinnedLi = grid.querySelector('li.grid__item:first-child');
        if (!pinnedLi) return;

        // Swap images
        pinnedLi.querySelectorAll('img').forEach(img => {
          const sizeMatch = img.src.match(/_(\d+x)\./);
          if (sizeMatch) {
            img.src = newSrcBase.replace(/\.(jpg|png|webp)/i, `_${sizeMatch[1]}.$1`);
          } else {
            img.src = newSrcBase;
          }
          img.srcset = '';
        });

        // Update all links to include variant parameter
        pinnedLi.querySelectorAll('a[href*="/products/"]').forEach(a => {
          try {
            const u = new URL(a.href, location.origin);
            u.searchParams.set('variant', variantID);
            a.href = u.toString();
          } catch (_) {}
        });
        
      } catch (error) {
        console.error('Variant update failed:', error);
      }
    }
    
  })().catch(error => {
    console.error('Fatal error:', error);
    grid.innerHTML = '<li class="grid__item" style="padding: 2rem; grid-column: 1/-1;">Error loading products</li>';
  });
  
})();